import React from 'react';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useTheme from '@material-ui/core/styles/useTheme';
import { TooltipProps as MuiTooltipProps } from '@material-ui/core/Tooltip';
import { IconButtonProps as MuiIconButtonProps } from '@material-ui/core/IconButton';
import { ButtonProps as MuiButtonProps } from '@material-ui/core/Button';
import makeStyles from '@material-ui/core/styles/makeStyles';

const useStyles = makeStyles((theme) => ({
  tooltip: {
    [theme.breakpoints.up(500)]: {
      maxWidth: 500,
    },
  },
  title: {
    fontSize: theme.typography.pxToRem(13),
  },
  popper: {
    zIndex: theme.zIndex.tooltip,
    [theme.breakpoints.down('xs')]: {
      width: '95%',
      maxWidth: theme.breakpoints.values.sm,
    },
  },
}));

type ResponsiveTooltipProps = {
  title: React.ReactNode,
  icon?: boolean,
  breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl',
  contentNode?: React.ReactNode,
  tooltipProps?: MuiTooltipProps,
  buttonProps?: MuiIconButtonProps | MuiButtonProps,
  classes?: { ['tooltip' | 'title' | 'popper']: string },
};

const ResponsiveTooltip = ({
  title,
  icon = false,
  breakpoint = 'md',
  contentNode,
  ...otherProps
}: ResponsiveTooltipProps) => {
  const classes = useStyles({ classes: otherProps.classes });

  const [open, setOpen] = React.useState(false);

  const handleToggle = () => setOpen((prevState) => !prevState);

  const handleClose = () => setOpen(false);

  const theme = useTheme();

  const matches = useMediaQuery(
    `(min-width:${theme.breakpoints.values[breakpoint]}px)`,
  );

  const tooltipRenderer = ({
    TooltipProps = {},
    ButtonProps = {},
  }: {
    TooltipProps?: MuiTooltipProps,
    ButtonProps?: MuiIconButtonProps | MuiButtonProps,
  } = {}) => (
    <Tooltip
      classes={{ tooltip: classes.tooltip }}
      title={<Typography className={classes.title}>{title}</Typography>}
      {...TooltipProps}
      {...(otherProps.tooltipProps || {})}
    >
      {React.createElement(
        icon ? IconButton : Button,
        { size: 'small', ...ButtonProps, ...otherProps.buttonProps },
        contentNode,
      )}
    </Tooltip>
  );

  if (matches) {
    return tooltipRenderer();
  } else {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        {tooltipRenderer({
          TooltipProps: {
            PopperProps: { disablePortal: true, className: classes.popper },
            onClose: handleClose,
            open,
            disableFocusListener: true,
            disableHoverListener: true,
            disableTouchListener: true,
          },
          ButtonProps: {
            onClick: handleToggle,
          },
        })}
      </ClickAwayListener>
    );
  }
};

export default ResponsiveTooltip;
